import { useEffect, useState } from "react";
import axios from "axios";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apps } from "@/lib/apps";
import { useRefreshStoreLinks, useStoreLinks } from "@/lib/storeLinks";
import { AppleIcon, PlayIcon } from "@/components/StoreBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LinkInput = ({ Icon, label, placeholder, value, onChange, testid }) => (
  <label className="block">
    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
    <div className="mt-2 flex items-center gap-2">
      <input type="url" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="min-w-0 flex-1 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm outline-none focus:border-[#22d3ee]" />
      {value && (
        <a href={value} target="_blank" rel="noopener noreferrer" aria-label={`Open ${label}`} data-testid={`${testid}-open`} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/15 text-white/60 hover:text-white">
          <ExternalLink className="h-4 w-4" />
        </a>
      )}
    </div>
  </label>
);

const AppLinks = ({ app, adminKey }) => {
  const saved = useStoreLinks(app.slug);
  const refresh = useRefreshStoreLinks();
  const [google, setGoogle] = useState(saved.google);
  const [apple, setApple] = useState(saved.apple);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setGoogle(saved.google);
    setApple(saved.apple);
  }, [saved.google, saved.apple]);
  const dirty = google !== saved.google || apple !== saved.apple;

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/apps/${app.slug}/links`, { google_play_url: google, app_store_url: apple }, { headers: { "X-Admin-Key": adminKey } });
      await refresh();
      toast.success(`${app.name} store links saved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save links.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`admin-links-${app.slug}`} className="glass rounded-3xl p-6">
      <div className="flex items-center gap-4">
        <img src={app.logo} alt="" className="h-12 w-12 rounded-2xl object-cover" style={{ transform: `scale(${app.logoScale})` }} />
        <div>
          <p className="font-display text-xl font-semibold">{app.name}</p>
          <p data-testid={`admin-links-status-${app.slug}`} className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
            {saved.google || saved.apple ? "Live on " + [saved.google && "Google Play", saved.apple && "App Store"].filter(Boolean).join(" + ") : "No store links yet"}
          </p>
        </div>
      </div>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <LinkInput Icon={PlayIcon} label="Google Play" placeholder="https://play.google.com/store/apps/details?id=…" value={google} onChange={setGoogle} testid={`admin-links-google-${app.slug}`} />
        <LinkInput Icon={AppleIcon} label="App Store" placeholder="https://apps.apple.com/app/…" value={apple} onChange={setApple} testid={`admin-links-apple-${app.slug}`} />
      </div>
      <button type="button" onClick={save} disabled={!dirty || busy} data-testid={`admin-links-save-${app.slug}`} className="btn-primary mt-6 !py-2.5 disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save links
      </button>
    </div>
  );
};

export const AdminStoreLinks = ({ adminKey }) => (
  <div data-testid="admin-store-links" className="space-y-4">
    <p className="max-w-2xl text-sm text-white/55">
      Paste the store links when an app goes live. The buttons on the website become real download links straight away. Leave a field empty and save to switch that store back to "Coming soon".
    </p>
    {apps.map((app) => <AppLinks key={app.slug} app={app} adminKey={adminKey} />)}
  </div>
);
